import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Download, Mail, Phone, MapPin, Github, Linkedin,
  ExternalLink, Award, Code, Briefcase, Trophy, Star, RefreshCw, User
} from 'lucide-react';
import Loading from '../components/common/Loading';
import { portfolioAPI } from '../services/api';
import toast from 'react-hot-toast';

const PortfolioPage = () => {
  const [portfolio, setPortfolio] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  // FETCH REAL PORTFOLIO DATA FROM BACKEND
  const fetchPortfolio = async () => {
    try {
      setIsLoading(true);
      console.log('🚀 Fetching REAL portfolio data from backend...');
      
      // ✅ REAL BACKEND CALL - NO SAMPLE DATA
      const portfolioData = await portfolioAPI.getPortfolio();
      console.log('✅ Portfolio loaded from backend:', portfolioData);
      
      setPortfolio(portfolioData);
      setLastUpdate(portfolioData.updated_at || new Date().toISOString());
      
      toast.success('Portfolio loaded successfully!');
      
    } catch (error) {
      console.error('❌ Failed to fetch portfolio:', error);
      toast.error('Failed to load portfolio. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // INITIAL LOAD
  useEffect(() => {
    fetchPortfolio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // REFRESH PORTFOLIO TO GET LATEST SETTINGS
  const handleRefresh = async () => {
    const toastId = toast.loading('Refreshing portfolio...');
    await fetchPortfolio();
    toast.dismiss(toastId);
    toast.success('Portfolio refreshed with latest settings!');
  };

  // EXPORT PORTFOLIO AS PDF
  const handleExport = () => {
    toast.success('Portfolio PDF export initiated!');
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Loading text="Loading your portfolio..." />
      </div>
    );
  }

  // No Portfolio State
  if (!portfolio || !portfolio.data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4">
        <div className="text-center">
          <User className="w-24 h-24 text-gray-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">No Portfolio Found</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Your portfolio will be generated from your settings and learning progress.
            Please complete your profile settings first.
          </p>
          <button 
            onClick={handleRefresh}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:scale-105 transition-transform inline-flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Load Portfolio
          </button>
        </div>
      </div>
    );
  }

  // EXTRACT REAL DATA FROM BACKEND RESPONSE
  const { 
    personal_info, 
    professional_summary, 
    education, 
    skills, 
    projects, 
    certifications, 
    hobbies 
  } = portfolio.data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-20 pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header with Refresh Button */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white">My Portfolio</h1>
            <p className="text-gray-400 mt-2">
              Auto-synced with your settings • Last updated: {lastUpdate ? new Date(lastUpdate).toLocaleString() : 'N/A'}
            </p>
            {portfolio.settings_synced && (
              <span className="inline-block mt-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                ✓ Synced with Settings
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 text-white rounded-lg border border-slate-600 transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all"
            >
              <Download className="w-4 h-4" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Personal Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 mb-8"
        >
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
              {personal_info?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            
            <div className="flex-1 w-full">
              {/* REAL USER NAME FROM BACKEND */}
              <h2 className="text-3xl font-bold text-white">{personal_info?.name || 'Your Name'}</h2>
              <p className="text-xl text-purple-400 mt-1">{personal_info?.title || 'Your Title'}</p>
              
              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {personal_info?.email && (
                  <div className="flex items-center gap-2 text-gray-300">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{personal_info.email}</span>
                  </div>
                )}
                {personal_info?.phone && (
                  <div className="flex items-center gap-2 text-gray-300">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span>{personal_info.phone}</span>
                  </div>
                )}
                {personal_info?.location && (
                  <div className="flex items-center gap-2 text-gray-300">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>{personal_info.location}</span>
                  </div>
                )}
                {personal_info?.career_goal && (
                  <div className="flex items-center gap-2 text-gray-300">
                    <Briefcase className="w-4 h-4 flex-shrink-0" />
                    <span>{personal_info.career_goal}</span>
                  </div>
                )}
              </div>

              {/* Social Links */}
              <div className="flex flex-wrap gap-3 mt-4">
                {personal_info?.linkedin && (
                  <a 
                    href={personal_info.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 rounded-lg text-gray-300 hover:text-white transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                    LinkedIn
                  </a>
                )}
                {personal_info?.github && (
                  <a 
                    href={personal_info.github} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 rounded-lg text-gray-300 hover:text-white transition-colors"
                  >
                    <Github className="w-5 h-5" />
                    GitHub
                  </a>
                )}
                {personal_info?.website && (
                  <a 
                    href={personal_info.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 rounded-lg text-gray-300 hover:text-white transition-colors"
                  >
                    <ExternalLink className="w-5 h-5" />
                    Website
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Professional Summary */}
          {professional_summary && (
            <div className="mt-6 pt-6 border-t border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-2">Professional Summary</h3>
              <p className="text-gray-300 leading-relaxed">{professional_summary}</p>
            </div>
          )}
        </motion.div>

        {/* Education Section */}
        {education && education.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 mb-8"
          >
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Award className="w-6 h-6 text-blue-400 mr-3" />
              Education
            </h3>
            <div className="space-y-6">
              {education.map((edu, index) => (
                <div key={index} className="p-4 bg-slate-700/30 rounded-xl">
                  <h4 className="text-lg font-semibold text-white">{edu.degree}</h4>
                  <p className="text-purple-400">{edu.institution}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400 mt-2">
                    <span>{edu.year}</span>
                    {edu.grade && <span>Grade: {edu.grade}</span>}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Skills Section */}
        {skills?.technical_skills && skills.technical_skills.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 mb-8"
          >
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Code className="w-6 h-6 text-blue-400 mr-3" />
              Technical Skills
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {skills.technical_skills.map((skill, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Code className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    <span className="font-medium text-white">{skill.name}</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                    skill.level === 'expert' ? 'bg-purple-500/20 text-purple-400' :
                    skill.level === 'advanced' ? 'bg-green-500/20 text-green-400' :
                    skill.level === 'intermediate' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {skill.level}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Projects Section */}
        {projects && projects.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 mb-8"
          >
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Briefcase className="w-6 h-6 text-green-400 mr-3" />
              Featured Projects
            </h3>
            <div className="grid gap-6">
              {projects.map((project, index) => (
                <div key={index} className="p-6 bg-slate-700/30 rounded-xl border border-slate-600/30 hover:border-purple-500/30 transition-all">
                  <div className="flex flex-col md:flex-row items-start justify-between mb-4 gap-4">
                    <div className="flex-1">
                      <h4 className="text-xl font-semibold text-white mb-2">{project.title}</h4>
                      <p className="text-gray-400">{project.description}</p>
                    </div>
                    {project.completion_percentage && (
                      <div className="flex items-center text-yellow-400 flex-shrink-0">
                        <Star className="w-4 h-4 mr-1" />
                        <span className="text-sm font-medium">{project.completion_percentage}%</span>
                      </div>
                    )}
                  </div>
                  
                  {project.technologies && project.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies.map((tech, idx) => (
                        <span key={idx} className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs">
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-4">
                    {project.github_url && (
                      <a 
                        href={project.github_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center text-gray-400 hover:text-white transition-colors"
                      >
                        <Github className="w-4 h-4 mr-2" />
                        Code
                      </a>
                    )}
                    {project.live_url && (
                      <a 
                        href={project.live_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center text-gray-400 hover:text-white transition-colors"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Live Demo
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Certifications */}
        {certifications && certifications.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 mb-8"
          >
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Trophy className="w-6 h-6 text-yellow-400 mr-3" />
              Certifications
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {certifications.map((cert, index) => (
                <div key={index} className="p-4 bg-slate-700/30 rounded-xl">
                  <div className="flex items-center mb-3">
                    <Trophy className="w-5 h-5 text-yellow-400 mr-2 flex-shrink-0" />
                    <h4 className="font-semibold text-white">{cert.title}</h4>
                  </div>
                  <p className="text-gray-400 text-sm mb-2">Issued by {cert.issuer}</p>
                  <p className="text-gray-500 text-xs">{cert.issue_date}</p>
                  {cert.verified && (
                    <span className="inline-block mt-2 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                      ✓ Verified
                    </span>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 mb-8"
        >
          <h3 className="text-2xl font-bold text-white mb-6">Additional Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {personal_info?.father_name && (
              <div>
                <p className="text-sm text-gray-400">Father&apos;s Name</p>
                <p className="font-medium text-white">{personal_info.father_name}</p>
              </div>
            )}
            {personal_info?.mother_name && (
              <div>
                <p className="text-sm text-gray-400">Mother&apos;s Name</p>
                <p className="font-medium text-white">{personal_info.mother_name}</p>
              </div>
            )}
            {personal_info?.date_of_birth && (
              <div>
                <p className="text-sm text-gray-400">Date of Birth</p>
                <p className="font-medium text-white">{personal_info.date_of_birth}</p>
              </div>
            )}
            {personal_info?.nationality && (
              <div>
                <p className="text-sm text-gray-400">Nationality</p>
                <p className="font-medium text-white">{personal_info.nationality}</p>
              </div>
            )}
            {hobbies && hobbies.length > 0 && (
              <div className="col-span-1 md:col-span-2">
                <p className="text-sm text-gray-400 mb-2">Hobbies & Interests</p>
                <div className="flex flex-wrap gap-2">
                  {hobbies.map((hobby, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
                      {hobby}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Stats Footer */}
        {portfolio.statistics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <div className="bg-slate-800/30 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 text-center">
              <p className="text-3xl font-bold text-white">{portfolio.statistics.total_skills || 0}</p>
              <p className="text-gray-400 text-sm">Skills</p>
            </div>
            <div className="bg-slate-800/30 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 text-center">
              <p className="text-3xl font-bold text-white">{portfolio.statistics.total_projects || 0}</p>
              <p className="text-gray-400 text-sm">Projects</p>
            </div>
            <div className="bg-slate-800/30 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 text-center">
              <p className="text-3xl font-bold text-white">{portfolio.statistics.total_certifications || 0}</p>
              <p className="text-gray-400 text-sm">Certifications</p>
            </div>
            <div className="bg-slate-800/30 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 text-center">
              <p className="text-3xl font-bold text-white">{portfolio.profile_completeness || 0}%</p>
              <p className="text-gray-400 text-sm">Completeness</p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PortfolioPage;
